import { RuleContext, ValueNode } from '@navstation-portal/reporting/data-model/ui';
import { evaluate, readCarryOverField, readReportField } from './evaluate';
import { toOption } from './option-lists';
import { firstValue, resolveValue } from './resolve-value';

/**
 * Resolves a value node to the value a carry-over writes into a field.
 *
 * It is the second reader over the one value tree. `resolveValue` answers for
 * `min`, `max` and `compare`, and it gives a number, a date or nothing,
 * because a bound that took a string would put a value on the control that the
 * control cannot apply. This reader answers for `carryOver`, and it gives
 * whatever the field holds: a select carries an option object, a text box
 * carries text, and a checkbox carries a boolean. The carry-over button writes
 * the value into the store with that type, for the same reason.
 *
 * The nodes that calculate read the same way in both: `dateOffset`, `sum`,
 * `difference`, `product`, `round` and `columnSum`. Each still demands a
 * number or a date, and it gives nothing for anything else.
 *
 * It gives undefined where the template has nothing to write: no node, a node
 * that reaches nothing, a field the previous report left unset or cleared, and
 * a `when` node that takes a branch with nothing in it. A carry-over that
 * wrote a null would clear the field the user is filling, which is the one
 * thing a button beside a field must never do.
 */
export function resolveCarryOverValue(node: ValueNode | undefined, context: RuleContext): unknown {
  if (node === undefined) {
    return undefined;
  }
  switch (node.type) {
    case 'const':
      // The template holds the value itself. A string stays a string here,
      // where a bound reads it as an ISO date, because a text box carries text.
      return node.value;
    case 'reportField':
      return orUndefined(readReportField(context, node.path));
    case 'carryOverField':
      return orUndefined(readCarryOverField(context, node.path));
    case 'dateOffset':
    case 'sum':
    case 'difference':
    case 'product':
    case 'round':
    case 'columnSum':
      // The node calculates, so both readers give the same answer for it.
      return resolveValue(node, context);
    case 'vesselField':
      // The node gives a number and nothing else, so both readers give the
      // same answer for it.
      return resolveValue(node, context);
    case 'when': {
      const branch = evaluate(node.when, context) ? node.then : node.else;
      return branch === undefined ? undefined : resolveCarryOverValue(branch, context);
    }
    case 'firstValue':
      // This reader walks the list, so the first value of any kind wins, and a
      // text box can fall back to a default text.
      return firstValue(node.values, (value) => resolveCarryOverValue(value, context));
    case 'option':
      return toOption(node.options, resolveCarryOverValue(node.value, context));
    default:
      // Every node type has a branch above. This guards a new type added
      // without a branch: the compiler fails here.
      return assertNever(node);
  }
}

function assertNever(node: never): never {
  throw new Error(`Unknown value node type: ${String((node as { type: string }).type)}`);
}

/**
 * Reads a null as nothing to carry.
 *
 * A field the user cleared holds null, and a field the report never held reads
 * undefined. Both mean the previous report has nothing to give, so both answer
 * the same way.
 */
function orUndefined(value: unknown): unknown {
  return value === null ? undefined : value;
}
